import React, { useEffect, useState } from "react";
import { FormField, CardSection } from "../Common/FormComponents";

// ── Styles ─────────────────────────────────────────────────────────────────────
const ms = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1060,
    padding: "1rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 640,
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  head: {
    padding: "1rem 1.25rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1d23",
    flexShrink: 0,
  },
  body: { padding: "1.25rem", overflowY: "auto", flex: 1 },
  foot: {
    padding: "0.875rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },
  btn: {
    fontSize: 12, fontWeight: 500, padding: "6px 16px", borderRadius: 6,
    border: "1px solid #d1d5db", background: "transparent", color: "#374151", cursor: "pointer",
  },
  btnPrimary: {
    fontSize: 12, fontWeight: 600, padding: "6px 18px", borderRadius: 6,
    border: "none", background: "#1E40AF", color: "#fff", cursor: "pointer",
  },
  closeBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 20, lineHeight: 1, color: "#6b7280", padding: "0 2px",
  },
  errorBanner: {
    fontSize: 12, color: "#991B1B", background: "#FEF2F2",
    border: "1px solid #FECACA", borderRadius: 8, padding: "8px 12px", marginBottom: "1rem",
  },
  spinner: {
    display: "inline-block", width: 12, height: 12,
    border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
    borderRadius: "50%", animation: "spin 0.7s linear infinite",
    verticalAlign: "middle", marginRight: 6,
  },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: "0.75rem", marginTop: "0.25rem" },
  validationMsg: { fontSize: 11, color: "#991B1B", marginTop: 3 },
};

const getTodayInputValue = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
};

function ConvertToManagementModal({ show, onHide, property, onConfirm, loading = false, error = "" }) {
  const [form, setForm] = useState({
    contractStartDate: getTodayInputValue(),
    leaseStart: "",
    leaseEnd: "",
    ongoingRate: "5",
    paidBy: "landlord",
    ownerName: "",
    ownerContact: "",
    ownerEmail: "",
  });
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (!show) return;
    const ti = property?.placementInfo?.tenantInfo || {};
    const landlord = property?.placementInfo?.landlordInfo || {};
    const existingRate = property?.pricing?.rentalCommission?.ongoingRate;
    setForm({
      contractStartDate: getTodayInputValue(),
      leaseStart: ti.leaseStartDate ? ti.leaseStartDate.slice(0, 10) : "",
      leaseEnd: ti.leaseEndDate ? ti.leaseEndDate.slice(0, 10) : "",
      ongoingRate: existingRate != null ? String(existingRate) : "5",
      paidBy: property?.pricing?.rentalCommission?.paidBy || "landlord",
      ownerName: landlord.landlordName || "",
      ownerContact: landlord.landlordContact || "",
      ownerEmail: "",
    });
    setShowValidation(false);
  }, [show, property]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => { if (loading) return; onHide?.(); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.contractStartDate || !form.leaseStart || !form.leaseEnd || !form.ownerName || !form.ownerContact) {
      setShowValidation(true);
      return;
    }
    onConfirm?.({ ...form, ongoingRate: parseFloat(form.ongoingRate) });
  };

  if (!show) return null;

  return (
    <div style={ms.overlay} onClick={handleClose}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={ms.modal} onClick={(e) => e.stopPropagation()}>
        <div style={ms.head}>
          Convert to Property Management
          {!loading && <button style={ms.closeBtn} onClick={handleClose}>×</button>}
        </div>

        <form onSubmit={handleSubmit} style={{ display: "contents" }}>
          <div style={ms.body}>
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: "1rem", lineHeight: 1.6 }}>
              This will convert the property from a one-time rental to a managed property. The
              existing tenant and placement history will be preserved.
            </p>

            {error && <div style={ms.errorBanner}>{error}</div>}

            <CardSection title="Management Contract">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.75rem" }}>
                <div>
                  <FormField label="Contract Start Date" name="contractStartDate" type="date" value={form.contractStartDate} onChange={handleChange} required disabled={loading} />
                  {showValidation && !form.contractStartDate && <div style={ms.validationMsg}>Required.</div>}
                </div>
                <div>
                  <FormField label="Lease Start Date" name="leaseStart" type="date" value={form.leaseStart} onChange={handleChange} required disabled={loading} />
                  {showValidation && !form.leaseStart && <div style={ms.validationMsg}>Required.</div>}
                </div>
                <div>
                  <FormField label="Lease End Date" name="leaseEnd" type="date" value={form.leaseEnd} onChange={handleChange} required disabled={loading} />
                  {showValidation && !form.leaseEnd && <div style={ms.validationMsg}>Required.</div>}
                </div>
              </div>
            </CardSection>

            <CardSection title="Commission Structure">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem" }}>
                <FormField
                  label="Ongoing Commission Rate (%)"
                  name="ongoingRate"
                  type="number"
                  value={form.ongoingRate}
                  onChange={handleChange}
                  required
                  min="0"
                  max="100"
                  step="0.1"
                  disabled={loading}
                  helpText="% of monthly rent, paid each month after placement."
                />
                <FormField
                  label="Paid By"
                  name="paidBy"
                  type="select"
                  value={form.paidBy}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  options={[
                    { value: "landlord", label: "Landlord" },
                    { value: "tenant", label: "Tenant" },
                    { value: "split", label: "Split" },
                  ]}
                />
              </div>
            </CardSection>

            <CardSection title="Owner / Landlord Information">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.75rem" }}>
                <div>
                  <FormField label="Owner Name" name="ownerName" value={form.ownerName} onChange={handleChange} required disabled={loading} />
                  {showValidation && !form.ownerName && <div style={ms.validationMsg}>Required.</div>}
                </div>
                <div>
                  <FormField label="Owner Contact" name="ownerContact" value={form.ownerContact} onChange={handleChange} required disabled={loading} />
                  {showValidation && !form.ownerContact && <div style={ms.validationMsg}>Required.</div>}
                </div>
                <FormField label="Owner Email (optional)" name="ownerEmail" type="email" value={form.ownerEmail} onChange={handleChange} disabled={loading} />
              </div>
            </CardSection>
          </div>

          <div style={ms.foot}>
            <button type="button" style={ms.btn} onClick={handleClose} disabled={loading}>Cancel</button>
            <button type="submit" style={ms.btnPrimary} disabled={loading}>
              {loading ? <><span style={ms.spinner} />Converting…</> : "Convert to Property Management"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConvertToManagementModal;
